import { useState, useEffect, useCallback } from 'react';
import { getTemps, deleteTemps, recalculerTMPTrimestre } from '../services/airtable';
import { useToast } from '../contexts/ToastContext';
import { useYear } from '../contexts/YearContext';
import { getAvailableYearsFromTemps } from '../utils/yearUtils';

/**
 * useTempsList - Hook pour gérer la liste des temps
 *
 * @param {number} refresh - Trigger pour rafraîchir les données
 */
export function useTempsList(refresh) {
  const { showToast } = useToast();
  const { selectedYear, setSelectedYear, availableYears, setAvailableYears } = useYear();

  // États principaux
  const [temps, setTemps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // États pour l'édition
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // États pour la suppression
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Charger les temps
  const loadTemps = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTemps();
      setTemps(data);
      setAvailableYears(getAvailableYearsFromTemps(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger au montage et au refresh
  useEffect(() => {
    loadTemps();
  }, [refresh, loadTemps]);

  // Recharger après reconnexion (session expirée → renouvelée)
  useEffect(() => {
    const handler = () => {
      setError(null);
      loadTemps();
    };
    window.addEventListener('session:renewed', handler);
    return () => window.removeEventListener('session:renewed', handler);
  }, [loadTemps]);

  // Ouvrir la modale d'édition
  const handleEdit = useCallback((record) => {
    setEditingRecord(record);
    setShowEditModal(true);
  }, []);

  // Ouvrir la modale de suppression
  const handleDeleteClick = useCallback((record) => {
    setDeletingRecord(record);
    setShowDeleteModal(true);
  }, []);

  // Fermer la modale de suppression
  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setDeletingRecord(null);
  }, []);

  // Confirmer la suppression
  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingRecord) return;

    setDeleting(true);
    try {
      // Récupérer les infos avant suppression pour recalculer le TMP
      const mois = deletingRecord.fields?.Mois;
      const annee = deletingRecord.fields?.Année;

      await deleteTemps(deletingRecord.id);

      // Recalculer le TMP du trimestre si les données sont disponibles
      if (mois && annee) {
        const trimestre = mois >= 1 && mois <= 3 ? 'Q1' :
                         mois >= 4 && mois <= 6 ? 'Q2' :
                         mois >= 7 && mois <= 9 ? 'Q3' : 'Q4';

        try {
          await recalculerTMPTrimestre(annee, trimestre);
          console.log(`✅ TMP recalculé pour ${annee}-${trimestre} après suppression`);
        } catch (tmpError) {
          console.error('❌ Erreur lors du recalcul du TMP:', tmpError);
        }
      }

      showToast('Temps supprimé avec succès', 'success');
      setShowDeleteModal(false);
      setDeletingRecord(null);
      loadTemps();
    } catch (err) {
      showToast('Erreur lors de la suppression: ' + err.message, 'error');
    } finally {
      setDeleting(false);
    }
  }, [deletingRecord, showToast, loadTemps]);

  // Fermer la modale d'édition
  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
    setEditingRecord(null);
  }, []);

  // Succès de l'édition
  const handleEditSuccess = useCallback(() => {
    setShowEditModal(false);
    setEditingRecord(null);
    showToast('Temps modifié avec succès', 'success');
    loadTemps();
  }, [showToast, loadTemps]);

  // Filtrer et grouper les temps par mois
  const getFilteredAndGroupedTemps = useCallback(() => {
    const tempsFiltres = temps.filter(record => record.fields.Année === selectedYear);

    if (tempsFiltres.length === 0) {
      return { isEmpty: true, tempsByMonth: {}, sortedMonths: [] };
    }

    // Grouper par mois
    const tempsByMonth = {};
    tempsFiltres.forEach(record => {
      const mois = record.fields.Mois;
      if (!tempsByMonth[mois]) tempsByMonth[mois] = [];
      tempsByMonth[mois].push(record);
    });

    const sortedMonths = Object.keys(tempsByMonth).sort((a, b) => parseInt(a) - parseInt(b));

    return { isEmpty: false, tempsByMonth, sortedMonths };
  }, [temps, selectedYear]);

  // Calculer les totaux d'un mois
  const getMonthTotals = useCallback((tempsOfMonth) => {
    const totalJours = tempsOfMonth.reduce((sum, record) => sum + (record.fields['Nombre de jours'] || 0), 0);
    const totalCA = tempsOfMonth.reduce((sum, record) => sum + (record.fields['CA calculé'] || 0), 0);
    return { totalJours, totalCA };
  }, []);

  return {
    // État principal
    temps,
    loading,
    error,
    selectedYear,
    availableYears,
    setSelectedYear,

    // Édition
    editingRecord,
    showEditModal,
    handleEdit,
    handleEditClose,
    handleEditSuccess,

    // Suppression
    deletingRecord,
    showDeleteModal,
    deleting,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,

    // Helpers
    getFilteredAndGroupedTemps,
    getMonthTotals
  };
}
